import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders, setAnneeAcademique } from './baseApi';

export interface AnneeAcademique {
  id: number;
  annee_debut: number;
  annee_fin: number;
  libelle: string;
  est_active: boolean;
  est_cloturee: boolean;
  date_debut_cours?: string | null;
  date_fin_cours?: string | null;
  date_debut_examens?: string | null;
  date_fin_examens?: string | null;
  date_debut_rattrapage?: string | null;
  date_fin_rattrapage?: string | null;
  date_creation?: string;
  date_modification?: string;
}

export interface AnneeAcademiqueList {
  id: number;
  libelle: string;
  est_active: boolean;
  est_cloturee: boolean;
}

export interface AnneeAcademiqueCreate {
  annee_debut: number;
  annee_fin: number;
  date_debut_cours?: string | null;
  date_fin_cours?: string | null;
  date_debut_examens?: string | null;
  date_fin_examens?: string | null;
  date_debut_rattrapage?: string | null;
  date_fin_rattrapage?: string | null;
}

export const anneeAcademiqueApi = createApi({
  reducerPath: 'anneeAcademiqueApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['AnneesAcademiques'],
  endpoints: (builder) => ({
    // Liste des années académiques
    getAnneesAcademiques: builder.query<AnneeAcademiqueList[], {
      est_active?: boolean;
      est_cloturee?: boolean;
    } | void>({
      query: (params) => ({
        url: '/annees-academiques/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['AnneesAcademiques'],
    }),

    // Détail d'une année
    getAnneeAcademique: builder.query<AnneeAcademique, number>({
      query: (id) => `/annees-academiques/${id}/`,
      providesTags: ['AnneesAcademiques'],
    }),

    // Année active
    getAnneeActive: builder.query<AnneeAcademique, void>({
      query: () => '/annees-academiques/active/',
      providesTags: ['AnneesAcademiques'],
    }),

    // Créer une année
    createAnneeAcademique: builder.mutation<AnneeAcademique, AnneeAcademiqueCreate>({
      query: (data) => ({
        url: '/annees-academiques/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AnneesAcademiques'],
    }),

    // Modifier une année
    updateAnneeAcademique: builder.mutation<AnneeAcademique, { id: number; data: Partial<AnneeAcademiqueCreate> }>({
      query: ({ id, data }) => ({
        url: `/annees-academiques/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['AnneesAcademiques'],
    }),

    // Supprimer une année
    deleteAnneeAcademique: builder.mutation<void, number>({
      query: (id) => ({
        url: `/annees-academiques/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AnneesAcademiques'],
    }),

    // Activer une année
    activerAnnee: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: `/annees-academiques/${id}/activer/`,
        method: 'POST',
      }),
      invalidatesTags: ['AnneesAcademiques'],
    }),

    // Clôturer une année
    cloturerAnnee: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: `/annees-academiques/${id}/cloturer/`,
        method: 'POST',
      }),
      invalidatesTags: ['AnneesAcademiques'],
    }),

    // Copier la structure vers une nouvelle année
    copierStructure: builder.mutation<{
      success: boolean;
      message: string;
      ues_copiees: number;
      matieres_copiees: number;
    }, {
      id: number;
      annee_destination_id?: number;
      annee_debut?: number;
      annee_fin?: number;
    }>({
      query: ({ id, ...data }) => ({
        url: `/annees-academiques/${id}/copier_structure/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AnneesAcademiques'],
    }),

    verifierSchema: builder.query<{
      annee_active: { id: number | null; libelle: string | null };
      schema_actif: string;
      schema_attendu: string | null;
      search_path: string;
      coherent: boolean;
      schemas_existants: string[];
      compteurs: Record<string, number | null>;
      message: string;
    }, void>({
      query: () => '/annees-academiques/verifier_schema/',
    }),
  }),
});

export const {
  useGetAnneesAcademiquesQuery,
  useGetAnneeAcademiqueQuery,
  useGetAnneeActiveQuery,
  useCreateAnneeAcademiqueMutation,
  useUpdateAnneeAcademiqueMutation,
  useDeleteAnneeAcademiqueMutation,
  useActiverAnneeMutation,
  useCloturerAnneeMutation,
  useCopierStructureMutation,
  useVerifierSchemaQuery,
} = anneeAcademiqueApi;
